import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { Observable, Subject } from 'rxjs';
import { filter, first, switchMap, take, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/auth-o3/shared/services/api.service';
import { AuthService } from 'src/app/auth-o3/shared/services/auth.service';
import { FormConfigService } from 'src/app/form-config/form-config.service';
import { CALL_EVENT_TYPE } from 'src/app/form-config/mockups/consts/call-event-type.const';
import { COUPON_TYPE } from 'src/app/form-config/mockups/consts/coupon-type.const';
import { Records } from 'src/app/form-config/model/records.type';
import { DataExportFormats } from 'src/app/static-data/models/interfaces/static-data';
import { STATIC_DATA_ENDPOINTS } from 'src/app/static-data/services/static-data-endpoints.const';
import { DcAlertService } from 'src/app/ui-kit/dc-alert/dc-alert.service';
import { ModalService } from 'src/app/ui-kit/dc-modal/modal.service';
import { ProductIdentifierModalService } from 'src/app/ui-kit/dc-product-identifier-modal/product-identifier-modal.service';
import { SplitButtonOption } from 'src/app/ui-kit/dc-split-button/dc-split-button.component';
import { environment } from 'src/environments/environment';
import { DOWNSIDE_TYPES } from '../model/consts/downside-type.const';
import { PRODUCT_CAPTURE_CONST } from '../model/consts/product-capture-key.const';
import { PayoffSchedule } from '../model/interfaces/payoff-schedule.interface';
import { ProductCaptureData } from '../model/interfaces/product-capture.interface';
import { ProductCaptureService } from '../services/product-capture.service';
import { GetProductCaptureFormPatchValue } from '../utils/get-product-capture-form-patch-value';
import { AnalyticsComponent } from './analytics/analytics.component';
import { ImportProductModalService } from '../import-product/import-product-modal.service';
import { IdentifierPoolsService } from "../../settings/identifiers-pool/identifier-pools.service";
import { IdentifierPoolsModalService } from "../../settings/identifiers-pool/identifier-pools-modal.service";

@Component({
  selector: 'app-product-capture-details',
  templateUrl: './product-capture-details.component.html',
  styleUrls: ['./product-capture-details.component.scss'],
})
export class ProductCaptureDetailsComponent implements OnInit, OnDestroy {
  downside;
  loading = true;
  mainSection:
    | 'overview'
    | 'analytics'
    | 'governanceChecks'
    | 'lifecycle'
    | 'documents';
  pageTitle;
  productCaptureDetail: ProductCaptureData;
  productCaptureId;
  productIdentifiersArr;
  productTypes: Records;
  rows = [];
  refreshDateTime: Date;

  userTenant$: Observable<string>;

  exportOptions: SplitButtonOption[] = [];

  private destroy$ = new Subject<boolean>();

  constructor(
    private formConfigService: FormConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private productCaptureService: ProductCaptureService,
    private modalService: ModalService,
    private productIdentifierModalService: ProductIdentifierModalService,
    private identifierPoolsService: IdentifierPoolsService,
    private identifierPoolsModalService: IdentifierPoolsModalService,
    private dcAlertService: DcAlertService,
    private authService: AuthService,
    private apiService: ApiService,
    private importProductModalService: ImportProductModalService
  ) {}

  @ViewChild(AnalyticsComponent) analyticsComponent: AnalyticsComponent;

  ngOnInit(): void {
    this.userTenant$ = this.authService.getUserTenant();
    this.formConfigService.setFormConfig(PRODUCT_CAPTURE_CONST);
    this.productTypes = this.formConfigService.getOptionsByFieldName(
      'productType',
      PRODUCT_CAPTURE_CONST
    );
    this.productCaptureDetail = { productName: 'Loading...' } as any;
    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap) => {
          const id = params.get('id');
          this.productCaptureId = id;
          this.pageTitle = 'Loading...';
          return this.productCaptureService.getProduct(this.productCaptureId);
        }),
        first()
      )
      .subscribe(
        (value) => {
          this.loading = false;
          if (value == undefined) {
            this.pageTitle = 'Product Not Found';
          } else {
            this.rows =
              (value?.summary?.schedule?.coupon
                ?.schedule as PayoffSchedule[]) || [];

            const post = GetProductCaptureFormPatchValue(value, true);
            this.productCaptureDetail = post;
            this.pageTitle = this.productCaptureDetail?.summary?.productName;
            this.mainSection = 'overview';
            this.productIdentifiersArr =
              this.productCaptureDetail?.legalForm?.productIdentifiers?.productIdentifiersArr;

            const downsideType = DOWNSIDE_TYPES.find(
              (item) =>
                item.id === post.payoff?.participation?.downside?.downsideType
            );
            this.downside = downsideType?.displayValue;
          }
        },
        (error) => {
          console.log(error);

          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );

    this.apiService
      .get(
        STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS_ENABLED,
        environment.api.staticData
      )
      .pipe(take(1))
      .subscribe((exportFormats: DataExportFormats[]) => {
        this.exportOptions = exportFormats.map((format) => ({
          label: format.formatName,
          value: format.id,
          onClick: () =>
            this.productCaptureService
              .exportDetails(this.productCaptureId, format.integratorName)
              .subscribe((response) => window.open(response)),
        }));
      });
  }

  changeMainSection(section): void {
    this.mainSection = section;
  }

  getDisplayValue(data): any {
    if (data == null || data === '') {
      return '-';
    } else {
      return data;
    }
  }

  getProductTypeDisplayValue(productType): string {
    if (productType == null || productType === '') {
      return '-';
    } else {
      const type = this.productTypes.find((item) => item.id === productType);
      return type?.displayValue || '-';
    }
  }

  getCouponTypeDisplayValue(couponType): string {
    if (couponType == null || couponType === '') {
      return '-';
    } else {
      const type = COUPON_TYPE.options.find((item) => item.id === couponType);
      return type.label;
    }
  }

  getCallEventTypeDisplayValue(callEventType): string {
    if (callEventType == null || callEventType === '') {
      return '-';
    } else {
      const type = CALL_EVENT_TYPE.options.find(
        (item) => item.id === callEventType
      );
      return type?.label || '';
    }
  }

  getFrequencyDisplayValue(value: string | null): string {
    if (!value) {
      return '-';
    }
    if (value === 'AT_MATURITY') {
      return 'At Maturity';
    }
    return value;
  }

  openDocuments(): void {
    this.changeMainSection('documents');
  }

  viewProductIdentifiersModal(): void {
    this.productIdentifierModalService
      .openDialog({
        title: 'Product Identifiers',
        data: this.productIdentifiersArr,
        canReallocate: true,
      })
      .then(
        (result) => {
          if (result === 'reallocate') {
            this.allocateProductIdentifiers();
          }
        },
        (error) => {
          console.log(error);
        }
      );
  }

  allocateProductIdentifiers(): void {
    this.loading = true;
    this.identifierPoolsService.allocateIdentifiers(this.productCaptureId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.dcAlertService.showAlert({
              type: 'success',
              message: `Identifiers allocated successfully`,
            });
            this.ngOnInit();
          },
          error: (error) => {
            this.dcAlertService.showAlert({
              type: 'danger',
              message: `Failed to allocate identifiers: ${error.message}`,
            });
            console.error(error);
            this.loading = false;
          }
        })
  }

  editDetailsLinkClick(e): void {
    this.router.navigateByUrl(
      '/product-capture/edit-product/' + this.productCaptureId
    );
  }

  openDeleteProductModal(): void {
    this.modalService
      .openDialog({
        title: 'Delete Product',
        body: `<p>Are you sure you want to delete this product?</p>
        <p>You will not be able to view this information once deleted.</p>
        `,
        buttons: {
          positive: 'Delete',
          negative: 'Cancel',
        },
        size: 'md',
      })
      .then(
        (result) => {
          if (result === '{"positive":1}') {
            this.deleteProduct();
          }
          console.log(result);
        },
        (error) => {
          console.log(error);
        }
      );
  }

  deleteProduct(): void {
    this.loading = true;

    this.productCaptureService.deleteProduct(this.productCaptureId).subscribe(
      () => {
        this.dcAlertService.showAlert({
          type: 'success',
          message: `${this.pageTitle} successfully deleted.`,
        });
        this.router.navigateByUrl('/product-capture');
      },
      (error) => {
        this.loading = false;

        this.dcAlertService.showAlert({
          type: 'danger',
          message: `Failed to delete product: ${error.message}`,
        });
        console.log(error);
      }
    );
  }

  refresh(): void {
    this.refreshDateTime = new Date();
    this.analyticsComponent.fetchAnalytics();
  }

  requestAnalytics(): void {
    this.analyticsComponent.activeReport.requestAnalyticsTriggerModal();
  }

  updateRefreshTimeFromChild(dateTime: Date) {
    this.refreshDateTime = dateTime;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }

  openUpdateProductModal() {
    this.importProductModalService
      .openUpdateModal(this.productCaptureDetail)
      .pipe(take(1))
      .subscribe((result: any) => {
        if (result === 'success') {
          this.dcAlertService.showAlert({
            type: 'success',
            message: `${this.pageTitle} updated successfully.`,
          });
          this.ngOnInit();
        }
      });
  }
}
